import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';

import { localized, t } from '@weavedev/lit-i18next';

import { iconCheckmark } from '@/components/icons';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles } from '@/styles';

import '@/components/personalisation/fg-personalisation';
import '../fg-configuration-print-option-summary';

import { PrintOptionMixin } from '../print-option-mixin';
@customElement('fg-configuration-print-option-small')
@localized()
export class FgConfigurationPrintOptionSmall extends PrintOptionMixin(
  LitElement,
) {
  render() {
    if (!this.printOption) {
      return null;
    }

    const {
      hasColorChoice,
      image,
      personalization,
      printPosition,
      description,
    } = this.printOption;

    const { type } = personalization || {};
    const { personalizable } = this.configurationContext;

    return html`
      <div class=${`print-option ${personalization && type ? 'finished' : ''}`}>
        <div class="print-option-content-wrapper">
          ${image
            ? html`
                <img
                  src=${image}
                  alt=${printPosition.name}
                  width="72"
                  height="72"
                />
              `
            : nothing}

          <div class="print-option-details-wrapper">
            <strong>${printPosition.name}</strong>
            <div class="print-option-details">
              <p>${description}</p>
            </div>
          </div>
        </div>
        ${personalization && type
          ? html`
              <div class="print-option-personalisation-details">
                <span>${iconCheckmark}</span>
                ${t('labels.logoTextUploaded')}
              </div>
            `
          : html`
              ${personalizable
                ? html`
                    <button
                      class="button button-secondary"
                      @click=${this.openPersonalisationDialog}
                    >
                      ${t('actions.uploadLogoText')}
                    </button>
                  `
                : nothing}
            `}
      </div>
      <fg-personalisation
        ${ref(this.fgPersonalisationDialogRef)}
        .printOption=${this.printOption}
        .hasColorChoice=${hasColorChoice}
        @personalisation-completed=${this.savePersonalisationData}
      >
        <fg-configuration-print-option-summary
          slot="print-option-summary"
          .option=${this.printOption}
        ></fg-configuration-print-option-summary>
      </fg-personalisation>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      .print-option {
        align-items: center;
        background-color: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        min-height: 88px;
        padding: 16px;
      }

      .print-option.finished {
        align-items: flex-start;
        background-color: var(
          --fg-success--light,
          var(--fg-fallback-success--light)
        );
        border: 1px solid var(--fg-success, var(--fg-fallback-success));
      }

      .print-option img {
        aspect-ratio: 64 / 64;
        flex-shrink: 0;
        margin-right: 16px;
        object-fit: contain;
      }

      .print-option button {
        width: 100%;
      }

      .print-option button {
        margin-top: 16px;
      }

      .print-option-details {
        display: flex;
        flex-direction: column;
        font-size: 16px;
        gap: 1px;
      }

      .print-option-details p {
        display: flex;
        flex-wrap: wrap;
        font-size: 0.875rem;
      }

      .print-option-details span.divider {
        margin-right: 2px;
      }

      strong {
        font-size: 1rem;
      }

      .print-option-content-wrapper {
        align-items: center;
        display: flex;
      }

      .print-option-details-wrapper {
        display: flex;
        flex-direction: column;
      }

      .print-option-personalisation-details {
        color: var(--fg-success--dark, var(--fg-fallback-success--dark));
        display: flex;
      }

      .print-option-personalisation-details span {
        margin-right: 5px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .print-option {
          flex-direction: row;
          padding: 0 16px;
        }

        .print-option button {
          margin-top: 0;
          width: auto;
        }

        .print-option-details p {
          font-size: 1rem;
        }
        .print-option-details-wrapper {
          padding-block: 8px;
        }

        .print-option.finished {
          align-items: center;
        }
      }
    `,
  ];
}
